import { useCallback, useEffect, useMemo, useState } from 'react';
import { frameFlags, frameRobots, frameTokens } from '../api.js';
import type { FlagView, FrameRobot, LogEntry, TokenView } from '../api.js';
import {
  afterArchiveStep, afterFlagStep, afterMissileStep, applyStep, ARCHIVE_STEPS,
  DESTRUCTION, expandMissileFlights, FLAG_STEPS, MISSILE_STEPS, playableSteps, shotFrom,
} from './useSteppedPlayback.js';
import type { Archive, DisplayRobot, Shot } from './useSteppedPlayback.js';

/**
 * A finished game played back from turn 1.
 *
 * The same material as `useReplay`, which replays ONE register: each log
 * entry carries a frame saying where every robot stood going into it, and
 * events saying what then happened. This walks every entry in order
 * instead of one, so nothing here re-evaluates a rule and the server is
 * not asked for anything but the log. A game played under older rules
 * therefore replays as it was played.
 *
 * The whole run is computed UP FRONT rather than by chaining timers.
 * Playing is then a single index moving along an array, which is what
 * makes pause and resume free -- and it means a beat can never be lost to
 * a timer that fired while the tab was in the background.
 *
 * Both halves of the board come from the frames. The robots move on their
 * events between one frame and the next; the tokens are taken as recorded
 * and moved only for a missile in flight, which is the one token whose
 * whole journey the events describe. NOTHING about the layer is inferred:
 * debris is laid with no event recording it, so a frame that carries no
 * tokens yields none, and the caller is expected to refuse that game
 * rather than draw a bare course as though it were the board.
 */

/**
 * Twice the pace of the live game, which steps every 600ms.
 *
 * A whole game is on the order of a thousand steps. At the live pace that
 * is a quarter of an hour of watching; at this one it is half that, which
 * is a replay somebody might sit through.
 */
const STEP_MS = 300;

interface Beat {
  /** Where every robot stands at this beat. Absent from the map means
   * not on the board -- destroyed, or not yet returned. */
  positions: Map<string, DisplayRobot>;
  /** What is lying on the board, or null where the frame this beat came
   * from predates the token layer being recorded. */
  tokens: TokenView[] | null;
  /**
   * Where the flags stand, or null where the frame does not say -- a
   * course whose flags have never moved, or a frame written before they
   * were recorded. The course layout is what to draw for either.
   *
   * Not the same thing as `flags` below, which is the robots.
   */
  courseFlags: FlagView[] | null;
  /** Where each robot's archive stands at this beat, moved at step E as
   * the register plays rather than at the frame that follows it. */
  archives: Map<string, Archive>;
  /**
   * The rest of what the frame said about each robot: powered down,
   * virtual, eliminated, where its archive stands.
   *
   * Held apart from `positions` because these change only when a frame
   * arrives, never on a step: no event in a register turns a robot
   * virtual halfway through its own move. Carrying them in `DisplayRobot`
   * would mean every step had to remember to preserve them.
   */
  flags: Map<string, FrameRobot>;
  /**
   * Robots wrecked so far in this entry, drawn struck through where they
   * died rather than simply disappearing.
   *
   * Cleared by the next frame, which is where a wreck stops being shown:
   * frames omit destroyed robots, so one that came back is at its archive
   * and one that did not is gone. That is the live game's rule too -- a
   * wreck belongs to the register it happened in and to nothing after.
   */
  wrecked: Set<string>;
  /** Which entry this beat came out of, indexing the array passed in. */
  entryIndex: number;
  /** The shot to draw at this beat, or null. A frame beat never has one:
   * a frame is the board standing still, and nothing fires on it. */
  shot: Shot | null;
}

/**
 * The whole replay, one beat per thing worth seeing.
 *
 * Every entry contributes its frame as a beat, including the entries that
 * move nothing -- a deal, a programming close, an end of turn. Those are
 * the turn's punctuation: they hold the board still for a beat while the
 * log beside it says what happened.
 *
 * `entries` must be OLDEST FIRST.
 */
export function beatsOf(entries: LogEntry[]): Beat[] {
  const beats: Beat[] = [];
  const running = new Map<string, DisplayRobot>();
  let tokens: TokenView[] | null = null;
  let courseFlags: FlagView[] | null = null;
  let archives = new Map<string, Archive>();
  let flags = new Map<string, FrameRobot>();
  let wrecked = new Set<string>();

  entries.forEach((entry, entryIndex) => {
    // The frame is the board going in, so it corrects any drift the
    // events left behind. Entries logged before frames were recorded
    // carry none; those carry on from wherever the last one finished,
    // which is the best that can be done and is right whenever nothing
    // happened in between.
    if (entry.frame) {
      running.clear();
      flags = new Map();
      wrecked = new Set();
      archives = new Map();
      for (const r of frameRobots(entry.frame)) {
        running.set(r.id, { x: r.x, y: r.y, facing: r.facing, damage: r.damage ?? 0 });
        flags.set(r.id, r);
        archives.set(r.id, { archiveMarker: r.archiveMarker, archiveFlag: r.archiveFlag });
      }
      tokens = frameTokens(entry.frame);
      courseFlags = frameFlags(entry.frame);
    }
    beats.push({
      positions: new Map(running), tokens, courseFlags, archives, flags, wrecked, entryIndex,
      shot: null,
    });

    // A missile is walked one square at a time, as the live playback
    // walks it: the engine records only the ends of a flight, and the
    // squares it passes THROUGH are worth seeing, since a missile goes
    // off on the first thing it meets rather than where it was aimed.
    const steps = expandMissileFlights(playableSteps(entry.events));
    for (const step of steps) {
      // A missile's events carry the FIRER's id, so handing one to
      // `applyStep` would drag the shooter across the board after its own
      // shell. They move the token layer and nothing else.
      if (MISSILE_STEPS.has(step.type)) {
        if (tokens) tokens = afterMissileStep(tokens, step);
      } else if (ARCHIVE_STEPS.has(step.type)) {
        // Step E, where the marker is taken: at the square the board has
        // the robot on, which is where it is standing to take it.
        archives = afterArchiveStep(archives, step, running.get(step.robotId));
      } else if (FLAG_STEPS.has(step.type)) {
        // A flag riding a belt. It moves on the beat the belts run, as it
        // does in the live game, so the frame that follows agrees with
        // what was drawn rather than correcting it.
        courseFlags = afterFlagStep(courseFlags, step);
      } else if (DESTRUCTION.has(step.type)) {
        // Left where it died and marked, rather than deleted -- which is
        // what `applyStep` would do, and what made a robot simply cease
        // to be drawn at the beat it was destroyed.
        wrecked = new Set(wrecked).add(step.robotId);
      } else {
        applyStep(running, step);
      }
      beats.push({
        positions: new Map(running), tokens, courseFlags, archives, flags, wrecked, entryIndex,
        shot: shotFrom(step, running),
      });
    }
  });

  return beats;
}

export interface GameReplayState {
  /** Positions to draw, or null before the first beat. */
  positions: Map<string, DisplayRobot> | null;
  /** The token layer to draw, or null before the first beat. */
  tokens: TokenView[] | null;
  /** The shot to draw at this beat, or null where nothing fired. */
  shot: Shot | null;
  /**
   * Where the flags stand at this beat, or null where the frames do not
   * say -- a course whose flags have never moved, or a game recorded
   * before they were written down. The course layout is what to draw for
   * either.
   *
   * Not the same thing as `flags` below, which is the robots.
   */
  courseFlags: FlagView[] | null;
  /** Where each robot's archive stands at this beat. Empty before the
   * first beat. */
  archives: Map<string, Archive>;
  /** What else the frame said about each robot, by id. Empty before the
   * first beat. */
  flags: Map<string, FrameRobot>;
  /** Robots to draw struck through where they died. Empty before the
   * first beat. */
  wrecked: Set<string>;
  /**
   * Whether every frame in this game recorded the board.
   *
   * False means at least one entry carries robots alone, which is a game
   * played before the layer was written down -- or one that straddles the
   * change. Nothing can recover it, so the caller should refuse to play
   * rather than show a course with nothing on it.
   */
  boardRecorded: boolean;
  /** The entry being played, for the heading. */
  entry: LogEntry | null;
  /** Everything played so far, NEWEST FIRST, which is the order the log
   * panel reads. */
  played: LogEntry[];
  playing: boolean;
  /** Every beat has been shown. */
  done: boolean;
  /** How far through, 0 to 1, for a progress bar. */
  progress: number;
  /**
   * Counts up each time Play is pressed.
   *
   * For the board's `cut`: playing again jumps from the last beat of the
   * run before back to turn 1, and that is a cut, not every robot
   * driving back across the course at once.
   */
  run: number;
  /** Play from the beginning, or resume where a pause left off. */
  play: () => void;
  pause: () => void;
  /**
   * One beat back, one beat on. Both stop the replay first: a step taken
   * while the clock is running is overwritten by the next tick before
   * anyone sees it.
   *
   * A beat is one STEP -- a square moved, a shot landing -- not a whole
   * register. Every beat is a complete picture of the board, so going
   * back is only the index moving; nothing is recomputed or unwound.
   */
  stepBack: () => void;
  stepForward: () => void;
  canStepBack: boolean;
  canStepForward: boolean;
}

export function useGameReplay(entries: LogEntry[], stepMs = STEP_MS): GameReplayState {
  const beats = useMemo(() => beatsOf(entries), [entries]);

  // EVERY frame, not the first: a game that was in progress when the
  // board began being recorded holds both shapes, and its early turns are
  // as unplayable as an old game's.
  const recorded = useMemo(() => (
    entries.length > 0
      && entries.every((e) => e.frame !== null && frameTokens(e.frame) !== null)
  ), [entries]);

  /**
   * Which beat is showing. -1 draws the game as it FINISHED.
   *
   * A game that can be played rests on beat 0, so the board opens at
   * turn 1 and Play runs on from what is already there. It used to rest
   * at -1, which opened on the last turn of the game and made the first
   * thing a replay showed the answer to it.
   *
   * -1 is what is left for the game that cannot be played: no frame of
   * it can be drawn, and the finished board is the only honest picture
   * there is.
   */
  const [at, setAt] = useState(-1);
  const [playing, setPlaying] = useState(false);
  const [run, setRun] = useState(0);

  // A log arriving, or a different game, abandons whatever was running:
  // an index into the old beats means nothing against the new ones.
  useEffect(() => {
    setAt(recorded ? 0 : -1);
    setPlaying(false);
  }, [beats, recorded]);

  useEffect(() => {
    if (!playing) return undefined;
    const timer = setInterval(
      () => setAt((i) => Math.min(i + 1, beats.length - 1)),
      stepMs,
    );
    return () => clearInterval(timer);
  }, [playing, beats.length, stepMs]);

  const done = beats.length > 0 && at >= beats.length - 1;

  // Stopped here rather than inside the interval, which would be setting
  // state from within another component's update.
  useEffect(() => {
    if (playing && done) setPlaying(false);
  }, [playing, done]);

  const play = useCallback(() => {
    if (beats.length === 0) return;
    // Back to the top from either end: from the finished board, and from
    // the last beat, where playing again beats sitting there doing
    // nothing. The index moves in THIS render, beside the `run` below --
    // left for the interval to move it a beat later, the cut is spent on
    // a render showing the same board and the robots drive to turn 1
    // under the old key.
    setAt((i) => (i < 0 || i >= beats.length - 1 ? 0 : i));
    // Bumped on a resume too, where it changes nothing: the positions are
    // the ones already on screen, so remounting them draws the same
    // picture.
    setRun((n) => n + 1);
    setPlaying(true);
  }, [beats.length]);

  const pause = useCallback(() => setPlaying(false), []);

  // Both read `at` rather than using a functional update: stepping
  // forward off the finished board bumps `run` as well, and a state
  // setter is no place to do a second thing.
  const stepBack = useCallback(() => {
    setPlaying(false);
    // -1 is "not started", not a beat before the first one, so there is
    // nothing behind it to step to.
    if (at > 0) setAt(at - 1);
  }, [at]);

  const stepForward = useCallback(() => {
    setPlaying(false);
    // Off the finished board and onto turn 1 is the same cut Play makes.
    if (at < 0) setRun((n) => n + 1);
    setAt(Math.min(at + 1, beats.length - 1));
  }, [at, beats.length]);

  const beat = at >= 0 ? beats[at] : null;

  const played = useMemo(() => (
    beat ? entries.slice(0, beat.entryIndex + 1).reverse() : []
  ), [entries, beat]);

  return {
    positions: beat ? beat.positions : null,
    tokens: beat ? beat.tokens : null,
    shot: beat ? beat.shot : null,
    courseFlags: beat ? beat.courseFlags : null,
    archives: beat ? beat.archives : new Map(),
    flags: beat ? beat.flags : new Map(),
    wrecked: beat ? beat.wrecked : new Set(),
    boardRecorded: recorded,
    entry: beat ? entries[beat.entryIndex] : null,
    played,
    playing,
    done,
    progress: beats.length > 0 && at >= 0 ? (at + 1) / beats.length : 0,
    run,
    play,
    pause,
    stepBack,
    stepForward,
    canStepBack: at > 0,
    canStepForward: beats.length > 0 && at < beats.length - 1,
  };
}
